import React, { useEffect, useContext, useState, useCallback } from 'react';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Checkbox } from '@sravni/react-design-system';
import { useIsMobile } from '@sravni/react-utils';
import { Rows, Row, Col } from '../../../../../../../landing.common';
import { CREDIT_SELECTION_ABC_EXPERIMENT_IDS, FORM_FIELDS } from '../../../../../../../../constants';
import { amountTransform, PhoneInput, TextInput, phoneTransform } from '../../../../../../../landing.common';
import { AgreementLink } from '../../../../../../../agreement.link';
import { schema } from './schema';
import { CardsContext } from '../../../../../../context';
import { useSmsAuth } from '../../../../../../../hooks/smsAuth';
import { FullNameSuggest } from '../../../../../../../form/ui/suggest/full_name';
import { useAnalytics, EventType } from 'components/hooks/analytics';
import { CARD_PURPOSES } from '../../../../../landing.content/components/top.form';
import { useFormData, useFormStore } from '../../../../../../../../store/selectors/formStore';
import { useDispatch } from 'react-redux';
import { extendFormData, extendFormStore } from '../../../../../../../../store/reducers/formStore';
import { onComplete } from './onComplete';
import { useFormOnBlur } from '../../../../../../../form/hooks/formHooks';
import { RegionField } from '../../../../../../../form.common/fields';
import { useExperiments } from '../../../../../../../../store/reducers/abData';
import { checkAbExperimentVariant } from '../../../../../../../../helpers';

type Props = {
    onNext: () => void;
};
/** Параметры кредита и авторизация */
export const Step1: React.FC<Props> = (props) => {
    const { onNext } = props;
    const isMobile = useIsMobile();
    const { pushEvent } = useAnalytics();
    const dispatch = useDispatch();
    const formData = useFormData();
    const { userId, withSilentAuth, autoRun } = useFormStore();
    const experiments = useExperiments();
    const isOptionalPatronymic = checkAbExperimentVariant(
        experiments,
        CREDIT_SELECTION_ABC_EXPERIMENT_IDS.OPTION_PATRONYMIC,
        '1',
    );

    const [code, setCode] = useState('');
    const { topFormAdditionalData, taskManager } = useContext(CardsContext);

    const {
        countdown,
        codeIsRequested,
        requestCode,
        checkCode,
        inProcess: authInProcess,
        errorText: authError,
    } = useSmsAuth();

    const needGetCode = !codeIsRequested && !userId;

    const form = useForm({
        resolver: yupResolver(schema()),
        mode: 'onSubmit',
        reValidateMode: 'onSubmit',
    });

    const {
        control,
        watch,
        formState: { errors, dirtyFields: _ },
        trigger,
        reset,
        setValue,
        getValues,
        handleSubmit,
    } = form;

    const [patronymic] = watch([FORM_FIELDS.MIDDLE_NAME]);

    useEffect(() => {
        reset(formData, { keepErrors: true, keepDirtyValues: true });
    }, [formData, reset]);

    useEffect(() => {
        if (autoRun) {
            schema().validate(formData).then(onSubmit).catch(onInvalid);
        }
    }, [autoRun]);

    const emitFirstStepAnalytics = useCallback(() => {
        const [region, salary, patronymic] = getValues([
            FORM_FIELDS.REGION,
            FORM_FIELDS.MONTHLY_SALARY,
            FORM_FIELDS.MIDDLE_NAME,
        ]);
        const purpose = CARD_PURPOSES.find((item) => item.value === topFormAdditionalData.purpose);
        pushEvent({
            event: EventType.mainEvent,
            eventAction: 'Шаг 1|Параметры',
            eventLabel: [
                `Доход - ${salary}`,
                `Город получения - ${region}`,
                `Цель кредитной карты - ${purpose ? purpose.label : ''}`,
                `Отчество - ${patronymic === null ? 'нет' : 'да'}`,
            ].join('|'),
        });
    }, [pushEvent, getValues, topFormAdditionalData.purpose]);

    const onContinueButtonClick = useCallback(() => {
        dispatch(extendFormData({ [FORM_FIELDS.AGREEMENT]: true }));
    }, []);

    const stepOnNext = useCallback(() => {
        taskManager.register(onComplete());
        emitFirstStepAnalytics();
        onNext();
    }, [emitFirstStepAnalytics, onNext, taskManager]);

    const onCheckCode = useCallback(
        async (code: string) => {
            const [phone, firstName, lastName, email] = getValues([
                FORM_FIELDS.PHONE,
                FORM_FIELDS.FIRST_NAME,
                FORM_FIELDS.LAST_NAME,
                FORM_FIELDS.EMAIL,
            ]);

            const freshUserId = await checkCode(
                {
                    phone,
                    firstName,
                    lastName,
                    email,
                    code,
                },
                withSilentAuth,
            );
            if (!freshUserId) {
                return;
            }
            dispatch(extendFormStore({ userId: freshUserId }));
            stepOnNext();
        },
        [withSilentAuth, getValues, checkCode, stepOnNext, dispatch],
    );

    const onSmsCodeChange: React.FormEventHandler<HTMLInputElement> = useCallback(
        async (event) => {
            const {
                currentTarget: { value },
            } = event;
            setCode(value);

            if (!isMobile && value.length === 4) {
                return onCheckCode(value);
            }
        },
        [isMobile, setCode, onCheckCode],
    );

    const requestCodeHandler = useCallback(() => {
        const requestFields = [FORM_FIELDS.PHONE, FORM_FIELDS.FULL_NAME, FORM_FIELDS.EMAIL];
        const hasErrors = Object.keys(errors).some((key) => requestFields.includes(key));
        if (hasErrors) {
            return;
        }

        pushEvent({
            event: EventType.mainEvent,
            eventAction: 'Получить код по СМС',
        });
        const [phone, firstName, email] = getValues([FORM_FIELDS.PHONE, FORM_FIELDS.FIRST_NAME, FORM_FIELDS.EMAIL]);
        const params = {
            name: firstName,
            phone,
            email,
            productName: 'Подбор кредитной карты',
        };
        return requestCode(params);
    }, [requestCode, errors, trigger, getValues]);

    const onSubmit: SubmitHandler<FE.FormData> = useCallback(
        async (validValues) => {
            if (codeIsRequested) {
                return onCheckCode(code);
            }

            if (needGetCode) {
                return requestCodeHandler();
            } else {
                dispatch(extendFormData(validValues));
                stepOnNext();
            }
        },
        [code, codeIsRequested, needGetCode, onCheckCode, requestCodeHandler, stepOnNext],
    );

    const onInvalid = () => {
        dispatch(extendFormStore({ autoRun: false }));
    };

    const onSelectFullName: React.ComponentProps<typeof FullNameSuggest>['onSelect'] = useCallback(
        async (option) => {
            setValue(FORM_FIELDS.FIRST_NAME, option.firstName || '');
            setValue(FORM_FIELDS.LAST_NAME, option.lastName || '');
            setValue(FORM_FIELDS.MIDDLE_NAME, option.patronymic || null);
            setValue(FORM_FIELDS.GENDER, option.gender || '');
            await trigger([FORM_FIELDS.LAST_NAME, FORM_FIELDS.FIRST_NAME, FORM_FIELDS.MIDDLE_NAME]);
        },
        [setValue],
    );

    const onChangeNoPatronymic: React.ComponentProps<typeof Checkbox>['onChange'] = useCallback(async (event) => {
        if (event.currentTarget.checked) {
            const fullName = [getValues(FORM_FIELDS.LAST_NAME), getValues(FORM_FIELDS.FIRST_NAME)]
                .filter((v) => v)
                .join(' ');
            setValue(FORM_FIELDS.MIDDLE_NAME, null);
            setValue(FORM_FIELDS.FULL_NAME, fullName);
        } else {
            setValue(FORM_FIELDS.MIDDLE_NAME, undefined);
        }
        await trigger(FORM_FIELDS.MIDDLE_NAME);
    }, []);

    const phoneIsDisabled = Boolean(userId) || codeIsRequested;
    const nextButtonDisabled = codeIsRequested && code.length < 4;
    const submitButtonText = isMobile && needGetCode ? 'Получить код' : 'Далее';
    const formOnBlur = useFormOnBlur(form, false);

    return (
        <form onBlur={formOnBlur} onSubmit={handleSubmit(onSubmit)}>
            <Rows>
                <Row>
                    <Col md-6>
                        <Controller
                            control={control}
                            name={FORM_FIELDS.MONTHLY_SALARY}
                            render={({ field }) => (
                                <TextInput
                                    {...field}
                                    label="Ежемесячный доход"
                                    value={amountTransform.input(field)}
                                    onChange={amountTransform.output(field)}
                                    errorMessage={errors[FORM_FIELDS.MONTHLY_SALARY]?.message}
                                />
                            )}
                        />
                    </Col>
                    <Col md-6>
                        <Controller
                            control={control}
                            name={FORM_FIELDS.REGION}
                            render={({ field }) => (
                                <RegionField field={field} form={form} customLabel="Город получения карты" />
                            )}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Controller
                            control={control}
                            name={FORM_FIELDS.FULL_NAME}
                            render={({ field: { onBlur, ...field } }) => (
                                <FullNameSuggest
                                    input={{
                                        ...field,
                                        label: 'Фамилия, имя, отчество',
                                        errorMessage:
                                            errors[FORM_FIELDS.FIRST_NAME]?.message ||
                                            errors[FORM_FIELDS.LAST_NAME]?.message ||
                                            errors[FORM_FIELDS.MIDDLE_NAME]?.message,
                                    }}
                                    onSelect={onSelectFullName}
                                    onChange={field.onChange}
                                    onBlur={onBlur}
                                />
                            )}
                        />
                    </Col>
                </Row>
                {isOptionalPatronymic ? (
                    <Row>
                        <Col>
                            <Checkbox checked={patronymic === null} onChange={onChangeNoPatronymic}>
                                Отчество отсутствует
                            </Checkbox>
                        </Col>
                    </Row>
                ) : null}
                <Row>
                    <Col>
                        <Controller
                            control={control}
                            name={FORM_FIELDS.EMAIL}
                            render={({ field }) => (
                                <TextInput
                                    {...field}
                                    label="Электронная почта"
                                    errorMessage={errors[FORM_FIELDS.EMAIL]?.message}
                                />
                            )}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col md-6>
                        <Controller
                            control={control}
                            name={FORM_FIELDS.PHONE}
                            render={({ field }) => (
                                <PhoneInput
                                    {...field}
                                    disabled={phoneIsDisabled}
                                    label="Мобильный телефон"
                                    onChange={phoneTransform.output(field)}
                                    errorMessage={errors[FORM_FIELDS.PHONE]?.message}
                                />
                            )}
                        />
                    </Col>
                    <Col md-6>
                        {codeIsRequested ? (
                            <div>
                                <TextInput
                                    value={code}
                                    onChange={onSmsCodeChange}
                                    label="Код из СМС"
                                    errorMessage={authError}
                                    disabled={authInProcess}
                                    maxLength={4}
                                />
                                <Button
                                    disabled={countdown > 0 || authInProcess}
                                    variant="text"
                                    size={36}
                                    color="blue"
                                    onClick={requestCodeHandler}>
                                    Запросить новый код
                                    {countdown > 0 && ` (${countdown})`}
                                </Button>
                            </div>
                        ) : null}
                    </Col>
                </Row>

                <Row>
                    <Col md-8>
                        <Controller
                            control={control}
                            name={FORM_FIELDS.AGREEMENT}
                            render={({ field: { value, ...field } }) => <AgreementLink checked={value} {...field} />}
                        />
                    </Col>
                    <Col md-4>
                        <Button
                            loading={authInProcess}
                            block
                            variant="primary"
                            type="submit"
                            onClick={onContinueButtonClick}
                            disabled={nextButtonDisabled}>
                            {submitButtonText}
                        </Button>
                    </Col>
                </Row>
            </Rows>
        </form>
    );
};
